import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Store, MapPin, Grid3x3, MessageSquare, Globe, Star, Tag,
  Mail, LogOut, Plus, Search, Trash2, Pencil, CheckCircle2, XCircle, BadgeCheck,
  Sparkles, Menu, ExternalLink, ShieldCheck, PauseCircle, Loader2, Crown, Wrench,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { BusinessForm } from "@/pages/admin/BusinessForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "businesses", label: "Businesses", icon: Store },
  { id: "services", label: "Services", icon: Wrench },
  { id: "service-categories", label: "Service Categories", icon: Grid3x3 },
  { id: "cities", label: "Cities", icon: MapPin },
  { id: "categories", label: "Categories", icon: Grid3x3 },
  { id: "enquiries", label: "Enquiries", icon: MessageSquare },
  { id: "leads", label: "Website Leads", icon: Globe },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "offers", label: "Offers", icon: Tag },
  { id: "premium", label: "Subscriptions", icon: Crown },
  { id: "claims", label: "Claims", icon: BadgeCheck },
  { id: "messages", label: "Messages", icon: Mail },
];

export default function AdminDashboard() {
  useSeo("Admin Dashboard | OyeBest", "OyeBest admin dashboard.");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/admin/service-categories").then((r) => setServiceCategories(r.data)).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-secondary/40 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0f1115] text-white transform transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="font-display text-2xl font-extrabold">Oye<span className="text-primary">Best</span></Link>
          <p className="text-xs text-white/40 mt-0.5">Admin Panel</p>
        </div>
        <nav className="p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => { setSection(n.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${section === n.id ? "bg-primary text-white" : "text-white/60 hover:bg-white/10"}`}
              data-testid={`admin-nav-${n.id}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 mt-auto absolute bottom-0 inset-x-0 border-t border-white/10">
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:bg-white/10" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-border px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="admin-sidebar-toggle"><Menu className="w-6 h-6" /></button>
            <h1 className="font-display text-xl font-bold capitalize">{NAV.find((n) => n.id === section)?.label}</h1>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="hidden sm:inline text-muted-foreground">{user?.email}</span>
            <Link to="/" target="_blank"><Button variant="outline" size="sm" className="rounded-full gap-1"><ExternalLink className="w-3.5 h-3.5" /> View Site</Button></Link>
          </div>
        </header>

        <div className="p-4 sm:p-6 max-w-6xl">
          {section === "dashboard" && <DashboardHome />}
          {section === "businesses" && <BusinessesSection categories={categories} cities={cities} />}
          {section === "services" && <BusinessesSection categories={categories} cities={cities} serviceCategories={serviceCategories} listingType="service" />}
          {section === "service-categories" && <ServiceCategoriesSection onChange={() => api.get("/admin/service-categories").then((r) => setServiceCategories(r.data))} />}
          {section === "cities" && <CitiesSection onChange={() => api.get("/admin/cities").then((r) => setCities(r.data))} />}
          {section === "categories" && <CategoriesSection onChange={() => api.get("/admin/categories").then((r) => setCategories(r.data))} />}
          {section === "enquiries" && <EnquiriesSection />}
          {section === "leads" && <LeadsSection />}
          {section === "reviews" && <ReviewsSection />}
          {section === "offers" && <OffersSection />}
          {section === "premium" && <SubscriptionsSection />}
          {section === "claims" && <ClaimsSection />}
          {section === "messages" && <MessagesSection />}
        </div>
      </div>
    </div>
  );
}

// ---------------- Dashboard Home ----------------
function StatCard({ label, value, icon: Icon, tone = "primary" }) {
  const tones = { primary: "bg-accent text-primary", blue: "bg-blue-50 text-blue-600", green: "bg-green-50 text-green-600", amber: "bg-amber-50 text-amber-600" };
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-center justify-between">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}><Icon className="w-5 h-5" /></span>
      </div>
      <p className="text-3xl font-display font-extrabold mt-3">{value ?? "—"}</p>
      <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function DashboardHome() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  const cards = [
    ["Total Businesses", s?.businesses, Store, "primary"],
    ["Service Providers", s?.services, Wrench, "primary"],
    ["Published", s?.published, CheckCircle2, "green"],
    ["Pending Approval", s?.pending, PauseCircle, "amber"],
    ["Verified", s?.verified, BadgeCheck, "blue"],
    ["Featured", s?.featured, Sparkles, "primary"],
    ["Business Owners", s?.owners, Crown, "amber"],
    ["Cities", s?.cities, MapPin, "blue"],
    ["Categories", s?.categories, Grid3x3, "primary"],
    ["Reviews", s?.reviews, Star, "amber"],
    ["Enquiries", s?.enquiries, MessageSquare, "green"],
    ["Website Leads", s?.website_leads, Globe, "primary"],
    ["Profile Views", s?.views, LayoutDashboard, "blue"],
    ["Call Clicks", s?.calls, Store, "green"],
    ["WhatsApp Clicks", s?.whatsapp, MessageSquare, "green"],
    ["Contact Messages", s?.contact_messages, Mail, "amber"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-stats">
      {cards.map(([l, v, i, t]) => <StatCard key={l} label={l} value={v} icon={i} tone={t} />)}
    </div>
  );
}

// ---------------- Businesses ----------------
const STATUS_TABS = ["all", "published", "pending", "verified", "featured", "draft", "suspended"];

function BusinessesSection({ categories, cities, serviceCategories = [], listingType = "business" }) {
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isService = listingType === "service";
  const noun = isService ? "service" : "business";

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/businesses", { params: { status: tab, listing_type: listingType } }).then((r) => setList(r.data)).finally(() => setLoading(false));
  }, [tab, listingType]);
  useEffect(() => { load(); }, [load]);

  const action = async (id, act) => {
    await api.patch(`/admin/businesses/${id}/action?action=${act}`);
    toast.success(`Done: ${act}`);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this business?")) return;
    await api.delete(`/admin/businesses/${id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = list.filter((b) => b.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center bg-white rounded-full border border-border px-3 h-10 flex-1 max-w-xs">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search businesses…" className="bg-transparent outline-none px-2 text-sm w-full" data-testid="admin-biz-search" />
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-full gap-2 font-semibold" data-testid="admin-add-business"><Plus className="w-4 h-4" /> Add {isService ? "Service" : "Business"}</Button>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 pb-1">
        {STATUS_TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 h-9 rounded-full text-sm font-medium capitalize whitespace-nowrap border ${tab === t ? "bg-primary text-white border-primary" : "bg-white border-border text-muted-foreground"}`} data-testid={`biz-tab-${t}`}>{t}</button>
        ))}
      </div>

      {loading ? <Loading /> : filtered.length === 0 ? <Empty text="No businesses found" /> : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <div key={b.id} className="rounded-2xl border border-border bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-biz-${b.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold">{b.name}</span>
                  <StatusBadge status={b.status} />
                  {b.verified && <Badge className="bg-blue-600 gap-1 rounded-full text-[10px]"><BadgeCheck className="w-3 h-3" />Verified</Badge>}
                  {b.featured && <Badge className="bg-primary gap-1 rounded-full text-[10px]"><Sparkles className="w-3 h-3" />Featured</Badge>}
                  {b.is_demo && <Badge variant="outline" className="rounded-full text-[10px]">Demo</Badge>}
                </div>
                <p className="text-sm text-muted-foreground truncate">{b.category_name} · {b.city_name} · {b.address}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {b.status === "pending" && <IconBtn onClick={() => action(b.id, "approve")} icon={CheckCircle2} label="Approve" tone="green" testid={`approve-${b.slug}`} />}
                {b.status === "pending" && <IconBtn onClick={() => action(b.id, "reject")} icon={XCircle} label="Reject" tone="red" />}
                <IconBtn onClick={() => action(b.id, b.verified ? "unverify" : "verify")} icon={BadgeCheck} label={b.verified ? "Unverify" : "Verify"} testid={`verify-${b.slug}`} />
                <IconBtn onClick={() => action(b.id, b.featured ? "unfeature" : "feature")} icon={Sparkles} label={b.featured ? "Unfeature" : "Feature"} testid={`feature-${b.slug}`} />
                {b.status !== "suspended" ? <IconBtn onClick={() => action(b.id, "suspend")} icon={PauseCircle} label="Suspend" tone="amber" /> : <IconBtn onClick={() => action(b.id, "restore")} icon={ShieldCheck} label="Restore" tone="green" />}
                <IconBtn onClick={() => { setEditing(b); setFormOpen(true); }} icon={Pencil} label="Edit" testid={`edit-${b.slug}`} />
                <IconBtn onClick={() => del(b.id)} icon={Trash2} label="Delete" tone="red" testid={`delete-${b.slug}`} />
              </div>
            </div>
          ))}
        </div>
      )}
      <BusinessForm open={formOpen} onOpenChange={setFormOpen} categories={categories} cities={cities} serviceCategories={serviceCategories} listingType={listingType} editing={editing} onSaved={load} />
    </div>
  );
}

// ---------------- Cities ----------------
function CitiesSection({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", state: "Uttar Pradesh", enabled: true, description: "", seo_title: "", seo_description: "" });

  const load = () => api.get("/admin/cities").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openForm = (city) => {
    setEditing(city);
    setF(city ? { ...city } : { name: "", state: "Uttar Pradesh", enabled: true, description: "", seo_title: "", seo_description: "" });
    setOpen(true);
  };
  const save = async () => {
    if (!f.name) { toast.error("City name required"); return; }
    try {
      if (editing) await api.put(`/admin/cities/${editing.slug}`, f);
      else await api.post("/admin/cities", f);
      toast.success("Saved"); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (slug) => { if (!window.confirm("Delete city?")) return; await api.delete(`/admin/cities/${slug}`); load(); onChange?.(); };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={() => openForm(null)} className="rounded-full gap-2 font-semibold" data-testid="admin-add-city"><Plus className="w-4 h-4" /> Add City</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((c) => (
          <div key={c.slug} className="rounded-2xl border border-border bg-white p-4" data-testid={`admin-city-${c.slug}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold">{c.name}</span>
              <Badge variant={c.enabled ? "default" : "outline"} className="rounded-full">{c.enabled ? "Enabled" : "Disabled"}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{c.state}</p>
            <p className="text-xs text-muted-foreground mt-1">/{c.slug}</p>
            <div className="flex gap-2 mt-3">
              <IconBtn onClick={() => openForm(c)} icon={Pencil} label="Edit" testid={`edit-city-${c.slug}`} />
              <IconBtn onClick={() => del(c.slug)} icon={Trash2} label="Delete" tone="red" />
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit City" : "Add City"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="City Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl h-11" data-testid="city-name" /></F>
            <F label="State"><Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="rounded-xl h-11" /></F>
            <F label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" rows={2} /></F>
            <F label="SEO Title"><Input value={f.seo_title} onChange={(e) => setF({ ...f, seo_title: e.target.value })} className="rounded-xl h-11" /></F>
            <F label="SEO Description"><Textarea value={f.seo_description} onChange={(e) => setF({ ...f, seo_description: e.target.value })} className="rounded-xl" rows={2} /></F>
            <div className="flex items-center gap-2"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="city-enabled" /><Label>Enabled</Label></div>
            <Button onClick={save} className="w-full rounded-full font-semibold" data-testid="city-save">Save City</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Categories ----------------
function CategoriesSection({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", icon: "store", description: "", order: 99 });

  const load = () => api.get("/admin/categories").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openForm = (cat) => { setEditing(cat); setF(cat ? { ...cat } : { name: "", icon: "store", description: "", order: 99 }); setOpen(true); };
  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    try {
      if (editing) await api.put(`/admin/categories/${editing.slug}`, f);
      else await api.post("/admin/categories", f);
      toast.success("Saved"); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (slug) => { if (!window.confirm("Delete category?")) return; await api.delete(`/admin/categories/${slug}`); load(); onChange?.(); };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={() => openForm(null)} className="rounded-full gap-2 font-semibold" data-testid="admin-add-category"><Plus className="w-4 h-4" /> Add Category</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((c) => (
          <div key={c.slug} className="rounded-2xl border border-border bg-white p-4 flex items-center justify-between" data-testid={`admin-cat-${c.slug}`}>
            <div><span className="font-bold">{c.name}</span><p className="text-xs text-muted-foreground">{c.count ?? 0} businesses</p></div>
            <div className="flex gap-2">
              <IconBtn onClick={() => openForm(c)} icon={Pencil} label="Edit" testid={`edit-cat-${c.slug}`} />
              <IconBtn onClick={() => del(c.slug)} icon={Trash2} label="Delete" tone="red" />
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Category Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl h-11" data-testid="cat-name" /></F>
            <F label="Icon (lucide name)"><Input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} className="rounded-xl h-11" placeholder="e.g. store, utensils" /></F>
            <F label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" rows={2} /></F>
            <F label="Order"><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} className="rounded-xl h-11" /></F>
            <Button onClick={save} className="w-full rounded-full font-semibold" data-testid="cat-save">Save Category</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Service Categories ----------------
function ServiceCategoriesSection({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", icon: "wrench", description: "", order: 99 });

  const load = () => api.get("/admin/service-categories").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openForm = (cat) => { setEditing(cat); setF(cat ? { ...cat } : { name: "", icon: "wrench", description: "", order: 99 }); setOpen(true); };
  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    try {
      if (editing) await api.put(`/admin/service-categories/${editing.slug}`, f);
      else await api.post("/admin/service-categories", f);
      toast.success("Saved"); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (slug) => { if (!window.confirm("Delete service category?")) return; await api.delete(`/admin/service-categories/${slug}`); load(); onChange?.(); };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={() => openForm(null)} className="rounded-full gap-2 font-semibold" data-testid="admin-add-service-category"><Plus className="w-4 h-4" /> Add Service Category</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((c) => (
          <div key={c.slug} className="rounded-2xl border border-border bg-white p-4 flex items-center justify-between" data-testid={`admin-svc-cat-${c.slug}`}>
            <div><span className="font-bold">{c.name}</span><p className="text-xs text-muted-foreground">{c.count ?? 0} providers</p></div>
            <div className="flex gap-2">
              <IconBtn onClick={() => openForm(c)} icon={Pencil} label="Edit" testid={`edit-svc-cat-${c.slug}`} />
              <IconBtn onClick={() => del(c.slug)} icon={Trash2} label="Delete" tone="red" />
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Service Category" : "Add Service Category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Category Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl h-11" data-testid="svc-cat-name" /></F>
            <F label="Icon (lucide name)"><Input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} className="rounded-xl h-11" placeholder="e.g. wrench, zap, wind" /></F>
            <F label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" rows={2} /></F>
            <F label="Order"><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} className="rounded-xl h-11" /></F>
            <Button onClick={save} className="w-full rounded-full font-semibold" data-testid="svc-cat-save">Save Category</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Enquiries ----------------
const ENQ_STATUS = ["New", "Contacted", "In Progress", "Converted", "Closed"];
function EnquiriesSection() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/enquiries").then((r) => setList(r.data)); }, []);
  const update = async (id, status) => { await api.patch(`/admin/enquiries/${id}`, { status }); setList((l) => l.map((x) => x.id === id ? { ...x, status } : x)); };
  if (list.length === 0) return <Empty text="No enquiries yet" />;
  return (
    <div className="space-y-3">
      {list.map((e) => (
        <div key={e.id} className="rounded-2xl border border-border bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`enquiry-${e.id}`}>
          <div className="flex-1">
            <p className="font-bold">{e.name} <span className="font-normal text-muted-foreground">· {e.mobile}</span></p>
            <p className="text-sm text-muted-foreground">For: {e.business_name || "—"}</p>
            {e.message && <p className="text-sm mt-1">{e.message}</p>}
            <p className="text-xs text-muted-foreground mt-1">{new Date(e.created_at).toLocaleString()}</p>
          </div>
          <StatusSelect value={e.status} options={ENQ_STATUS} onChange={(v) => update(e.id, v)} />
        </div>
      ))}
    </div>
  );
}

// ---------------- Website Leads ----------------
const LEAD_STATUS = ["New", "Contacted", "Interested", "Demo Sent", "Payment Pending", "Completed", "Rejected"];
function LeadsSection() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/website-leads").then((r) => setList(r.data)); }, []);
  const update = async (id, status) => { await api.patch(`/admin/website-leads/${id}`, { status }); setList((l) => l.map((x) => x.id === id ? { ...x, status } : x)); };
  if (list.length === 0) return <Empty text="No website leads yet" />;
  return (
    <div className="space-y-3">
      {list.map((e) => (
        <div key={e.id} className="rounded-2xl border border-border bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`lead-${e.id}`}>
          <div className="flex-1">
            <p className="font-bold">{e.business_name} <span className="font-normal text-muted-foreground">· {e.mobile}</span></p>
            <p className="text-sm text-muted-foreground">{e.owner_name} · {e.city} · {e.category}</p>
            {e.requirements && <p className="text-sm mt-1">Req: {e.requirements}</p>}
            {e.budget && <p className="text-sm text-muted-foreground">Budget: {e.budget}</p>}
            <p className="text-xs text-muted-foreground mt-1">{new Date(e.created_at).toLocaleString()}</p>
          </div>
          <StatusSelect value={e.status} options={LEAD_STATUS} onChange={(v) => update(e.id, v)} />
        </div>
      ))}
    </div>
  );
}

// ---------------- Reviews ----------------
function ReviewsSection() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, status) => { await api.patch(`/admin/reviews/${id}`, { status }); toast.success(status); load(); };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };
  if (list.length === 0) return <Empty text="No reviews yet" />;
  return (
    <div className="space-y-3">
      {list.map((r) => (
        <div key={r.id} className="rounded-2xl border border-border bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`review-${r.id}`}>
          <div className="flex-1">
            <p className="font-bold">{r.name} <span className="text-amber-500">{"★".repeat(r.rating)}</span></p>
            {r.text && <p className="text-sm text-muted-foreground mt-1">{r.text}</p>}
            <StatusBadge status={r.status} />
          </div>
          <div className="flex gap-2">
            {r.status !== "approved" && <IconBtn onClick={() => act(r.id, "approved")} icon={CheckCircle2} label="Approve" tone="green" />}
            {r.status !== "hidden" && <IconBtn onClick={() => act(r.id, "hidden")} icon={XCircle} label="Hide" tone="amber" />}
            <IconBtn onClick={() => del(r.id)} icon={Trash2} label="Delete" tone="red" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------- Offers ----------------
function OffersSection() {
  const [list, setList] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ business_id: "", title: "", description: "", discount: "", start_date: "", end_date: "", terms: "" });
  const load = () => api.get("/admin/offers").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/admin/businesses").then((r) => setBusinesses(r.data)); }, []);
  const save = async () => {
    if (!f.business_id || !f.title) { toast.error("Business and title required"); return; }
    await api.post("/admin/offers", f); toast.success("Offer added"); setOpen(false); setF({ business_id: "", title: "", description: "", discount: "", start_date: "", end_date: "", terms: "" }); load();
  };
  const del = async (id) => { await api.delete(`/admin/offers/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={() => setOpen(true)} className="rounded-full gap-2 font-semibold" data-testid="admin-add-offer"><Plus className="w-4 h-4" /> Add Offer</Button></div>
      {list.length === 0 ? <Empty text="No offers yet" /> : (
        <div className="space-y-3">
          {list.map((o) => (
            <div key={o.id} className="rounded-2xl border border-border bg-white p-4 flex items-center justify-between" data-testid={`offer-${o.id}`}>
              <div><p className="font-bold">{o.title} {o.discount && <span className="text-primary">· {o.discount}</span>}</p><p className="text-sm text-muted-foreground">{o.business_name} · {o.description}</p></div>
              <IconBtn onClick={() => del(o.id)} icon={Trash2} label="Delete" tone="red" />
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Add Offer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Business">
              <Select value={f.business_id} onValueChange={(v) => setF({ ...f, business_id: v })}>
                <SelectTrigger className="rounded-xl h-11" data-testid="offer-business"><SelectValue placeholder="Select business" /></SelectTrigger>
                <SelectContent>{businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl h-11" data-testid="offer-title" /></F>
            <F label="Discount"><Input value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} className="rounded-xl h-11" placeholder="e.g. 20% OFF" /></F>
            <F label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" rows={2} /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Start Date"><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="rounded-xl h-11" /></F>
              <F label="End Date"><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="rounded-xl h-11" /></F>
            </div>
            <Button onClick={save} className="w-full rounded-full font-semibold" data-testid="offer-save">Save Offer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Messages ----------------
function MessagesSection() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/contact-messages").then((r) => setList(r.data)); }, []);
  if (list.length === 0) return <Empty text="No messages yet" />;
  return (
    <div className="space-y-3">
      {list.map((m) => (
        <div key={m.id} className="rounded-2xl border border-border bg-white p-4" data-testid={`message-${m.id}`}>
          <p className="font-bold">{m.name} <span className="font-normal text-muted-foreground">· {m.mobile} {m.email && `· ${m.email}`}</span></p>
          {m.subject && <p className="text-sm font-medium mt-0.5">{m.subject}</p>}
          <p className="text-sm text-muted-foreground mt-1">{m.message}</p>
          <p className="text-xs text-muted-foreground mt-1">{new Date(m.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------- Shared bits ----------------
function SubscriptionsSection() {
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [bizQuery, setBizQuery] = useState("");
  const [results, setResults] = useState([]);
  const load = () => { api.get("/admin/subscriptions").then((r) => setSubs(r.data)); api.get("/admin/plans").then((r) => setPlans(r.data)); };
  useEffect(() => { load(); }, []);
  const isActive = (s) => s.status === "active" && (!s.expiry_date || new Date(s.expiry_date) > new Date());
  const activate = async (bid, planId) => { await api.patch(`/admin/subscriptions/${bid}?action=activate&plan_id=${planId}`); toast.success("Premium activated"); load(); };
  const deactivate = async (bid) => { await api.patch(`/admin/subscriptions/${bid}?action=deactivate`); toast.success("Premium deactivated"); load(); };
  const searchBiz = (q) => { setBizQuery(q); if (q.length < 2) { setResults([]); return; } api.get("/admin/businesses", { params: { status: "all" } }).then((r) => setResults(r.data.filter((b) => b.name.toLowerCase().includes(q.toLowerCase())).slice(0, 6))); };
  const savePlan = async (p) => { await api.put(`/admin/plans/${p.id}`, { label: p.label, amount: Number(p.amount), currency: p.currency, days: Number(p.days), active: p.active }); toast.success("Plan saved"); };

  const active = subs.filter(isActive);
  const inactive = subs.filter((s) => !isActive(s));

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        {[["Active", active.length, "green"], ["Expired / Cancelled", inactive.length, "amber"], ["Total", subs.length, "primary"]].map(([l, v, t]) => (
          <div key={l} className="rounded-2xl border border-border bg-white p-4"><p className="text-2xl font-display font-extrabold">{v}</p><p className="text-sm text-muted-foreground">{l}</p></div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-white p-4">
        <p className="font-semibold mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" /> Plans & Pricing</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {plans.map((p, i) => (
            <div key={p.id} className="rounded-xl border border-border p-3 space-y-2" data-testid={`plan-${p.id}`}>
              <Input value={p.label} onChange={(e) => setPlans((ps) => ps.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="rounded-lg h-9" />
              <div className="flex gap-2"><Input type="number" value={p.amount} onChange={(e) => setPlans((ps) => ps.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="rounded-lg h-9" /><Input type="number" value={p.days} onChange={(e) => setPlans((ps) => ps.map((x, j) => j === i ? { ...x, days: e.target.value } : x))} className="rounded-lg h-9 w-20" title="days" /><Button size="sm" onClick={() => savePlan(p)} className="rounded-full" data-testid={`save-plan-${p.id}`}>Save</Button></div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-4">
        <p className="font-semibold mb-2">Manually activate Premium</p>
        <input value={bizQuery} onChange={(e) => searchBiz(e.target.value)} placeholder="Search a business…" className="w-full bg-secondary rounded-full px-4 h-10 text-sm outline-none" data-testid="sub-biz-search" />
        {results.map((b) => (
          <div key={b.id} className="flex items-center justify-between mt-2 text-sm">
            <span>{b.name} {b.premium && <span className="text-amber-600 font-semibold">· Premium</span>}</span>
            {b.premium ? <IconBtn onClick={() => deactivate(b.id)} icon={XCircle} label="Deactivate" tone="red" /> : <Button size="sm" className="rounded-full h-8" onClick={() => activate(b.id, "premium_monthly")} data-testid={`activate-${b.slug}`}>Activate</Button>}
          </div>
        ))}
      </div>

      {subs.length === 0 ? <Empty text="No subscriptions yet" /> : (
        <div className="space-y-3">
          {subs.map((s) => (
            <div key={s.business_id} className="rounded-2xl border border-border bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`sub-${s.business_id}`}>
              <div className="flex-1">
                <div className="font-bold flex items-center gap-2">{s.business_name} <Badge className={`rounded-full text-[10px] ${isActive(s) ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{isActive(s) ? "Active" : s.status}</Badge></div>                <p className="text-sm text-muted-foreground">{s.plan_label} · ₹{s.amount} · payment: {s.payment_status}</p>
                <p className="text-xs text-muted-foreground">Start {s.start_date ? new Date(s.start_date).toLocaleDateString() : "—"} · Expiry {s.expiry_date ? new Date(s.expiry_date).toLocaleDateString() : "—"}</p>
              </div>
              {isActive(s) ? <IconBtn onClick={() => deactivate(s.business_id)} icon={XCircle} label="Deactivate" tone="red" testid={`sub-deactivate-${s.business_id}`} />
                : <IconBtn onClick={() => activate(s.business_id, s.plan_id || "premium_monthly")} icon={CheckCircle2} label="Activate" tone="green" testid={`sub-activate-${s.business_id}`} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClaimsSection() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/claims").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => { await api.patch(`/admin/claims/${id}?action=${action}`); toast.success(action === "approve" ? "Claim approved" : "Claim rejected"); load(); };
  if (list.length === 0) return <Empty text="No claims yet" />;
  return (
    <div className="space-y-3">
      {list.map((c) => (
        <div key={c.id} className="rounded-2xl border border-border bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`claim-${c.id}`}>
          <div className="flex-1">
            <p className="font-bold">{c.business_name} <StatusBadge status={c.status} /></p>
            <p className="text-sm text-muted-foreground">{c.owner_name} · {c.owner_email} · {c.phone}</p>
            {c.message && <p className="text-sm mt-1">{c.message}</p>}
            <p className="text-xs text-muted-foreground mt-1">{new Date(c.created_at).toLocaleString()}</p>
          </div>
          {c.status === "pending" && (
            <div className="flex gap-2">
              <IconBtn onClick={() => act(c.id, "approve")} icon={CheckCircle2} label="Approve" tone="green" testid={`claim-approve-${c.id}`} />
              <IconBtn onClick={() => act(c.id, "reject")} icon={XCircle} label="Reject" tone="red" testid={`claim-reject-${c.id}`} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-sm font-semibold">{label}</Label>{children}</div>; }
function Loading() { return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>; }
function Empty({ text }) { return <div className="text-center py-16 text-muted-foreground rounded-2xl border border-dashed border-border bg-white">{text}</div>; }

function StatusBadge({ status }) {
  const map = { published: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", draft: "bg-gray-100 text-gray-600", suspended: "bg-red-100 text-red-600", rejected: "bg-red-100 text-red-600", approved: "bg-green-100 text-green-700", hidden: "bg-gray-100 text-gray-600" };
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${map[status] || "bg-gray-100 text-gray-600"}`}>{status}</span>;
}

function IconBtn({ onClick, icon: Icon, label, tone, testid }) {
  const tones = { red: "text-red-600 hover:bg-red-50", green: "text-green-600 hover:bg-green-50", amber: "text-amber-600 hover:bg-amber-50" };
  return (
    <button onClick={onClick} title={label} data-testid={testid} className={`flex items-center gap-1 px-2.5 h-8 rounded-lg border border-border text-xs font-medium bg-white ${tones[tone] || "text-foreground hover:bg-secondary"}`}>
      <Icon className="w-3.5 h-3.5" /> <span className="hidden md:inline">{label}</span>
    </button>
  );
}

function StatusSelect({ value, options, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-40 h-10 rounded-full bg-secondary shrink-0" data-testid="status-select"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}
